import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { LoginResponse, AuthUser, AuthTokens } from '@aegis/zod-schemas'
import type { ResolvedPermission } from './permissions'
import { apiFetch } from './api-client'

export type { AuthUser } from '@aegis/zod-schemas'

export interface AuthState {
  accessToken: string | null
  refreshToken: string | null
  user: AuthUser | null
  permissions: ResolvedPermission[]
  isAuthenticated: boolean
  /** Send a passwordless login link to the supplied email (always 200). */
  requestLoginLink: (email: string) => Promise<void>
  /** Consume a single-use magic-link token; sets tokens + fetches permissions. */
  consumeLoginLink: (token: string) => Promise<void>
  /** Exchange a Personal Access Token (CLI / agent flow); same outcome as consumeLoginLink. */
  loginWithPat: (token: string) => Promise<void>
  logout: () => Promise<void>
  refresh: () => Promise<void>
  fetchPermissions: () => Promise<void>
  fetchCurrentUser: () => Promise<void>
  clearAuth: () => void
}

function applyTokens(tokens: AuthTokens, user: AuthUser) {
  return {
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
    user,
    isAuthenticated: true,
  }
}

const CLEARED = {
  accessToken: null,
  refreshToken: null,
  user: null,
  permissions: [] as ResolvedPermission[],
  isAuthenticated: false,
} as const

const STORAGE_KEY = 'aegis-auth'

function readPersistedRefreshToken(): string | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { state?: { refreshToken?: string | null } }
    return parsed?.state?.refreshToken ?? null
  } catch {
    return null
  }
}

// Waits up to `timeoutMs` for another tab to write a refresh token different
// from `attempted`. Resolves to the fresh token if seen, or null on timeout.
// Listens to the `storage` event AND polls (storage event is the fast path;
// poll is a backstop in case the event is missed under tab scheduling
// pressure). Cancels both sources on the first hit.
function waitForRotatedRefreshToken(
  attempted: string,
  timeoutMs = 2000,
): Promise<string | null> {
  if (typeof window === 'undefined') return Promise.resolve(null)
  return new Promise((resolve) => {
    const immediate = readPersistedRefreshToken()
    if (immediate && immediate !== attempted) {
      resolve(immediate)
      return
    }
    let settled = false
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY || settled) return
      const stored = readPersistedRefreshToken()
      if (stored && stored !== attempted) finish(stored)
    }
    const pollId = window.setInterval(() => {
      if (settled) return
      const stored = readPersistedRefreshToken()
      if (stored && stored !== attempted) finish(stored)
    }, 100)
    const timeoutId = window.setTimeout(() => finish(null), timeoutMs)
    function finish(value: string | null) {
      if (settled) return
      settled = true
      window.removeEventListener('storage', onStorage)
      window.clearInterval(pollId)
      window.clearTimeout(timeoutId)
      resolve(value)
    }
    window.addEventListener('storage', onStorage)
  })
}

export const useAuthStore = create<AuthState>()(persist((set, get) => ({
  ...CLEARED,

  requestLoginLink: async (email) => {
    await apiFetch('/auth/login-link/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  },

  consumeLoginLink: async (token) => {
    const { tokens, user } = await apiFetch<LoginResponse>('/auth/login-link/consume', {
      method: 'POST',
      body: JSON.stringify({ token }),
    })
    set(applyTokens(tokens, user))
    await get().fetchCurrentUser().catch(() => {})
    try {
      const permissions = await apiFetch<ResolvedPermission[]>('/rbac/me/permissions')
      set({ permissions })
    } catch {
      // Non-blocking: permissions re-fetched on layout mount
    }
  },

  loginWithPat: async (token) => {
    const { tokens, user } = await apiFetch<LoginResponse>('/auth/access-token/exchange', {
      method: 'POST',
      body: JSON.stringify({ token }),
    })
    set(applyTokens(tokens, user))
    await get().fetchCurrentUser().catch(() => {})
    try {
      const permissions = await apiFetch<ResolvedPermission[]>('/rbac/me/permissions')
      set({ permissions })
    } catch {
      // Non-blocking
    }
  },

  logout: async () => {
    const { refreshToken } = get()
    try {
      if (refreshToken) {
        await apiFetch('/auth/logout', {
          method: 'POST',
          body: JSON.stringify({ refreshToken }),
        })
      }
    } finally {
      set(CLEARED)
      void import('@/lib/analytics').then((m) => m.resetAnalytics()).catch(() => {})
    }
  },

  refresh: async () => {
    // Cross-tab pre-flight: if another tab has already rotated the refresh
    // token, our in-memory copy is stale. Re-hydrate from localStorage and
    // skip the network call — calling /auth/refresh with the stale token
    // would hit TOKEN_REVOKED (atomic rotation guarantees only one wins).
    if (typeof window !== 'undefined') {
      const stored = readPersistedRefreshToken()
      const inMem = get().refreshToken
      if (stored && inMem && stored !== inMem) {
        await useAuthStore.persist.rehydrate()
        return
      }
    }
    const { refreshToken } = get()
    if (!refreshToken) {
      set(CLEARED)
      return
    }
    // Capture for the post-failure re-check: when A and B start with the
    // same R0 and both call /auth/refresh, the loser arrives here in catch.
    // Before clearing auth, we wait for the winner's storage write to land
    // and adopt the new tokens instead of bouncing to /login.
    const attemptedRefreshToken = refreshToken
    try {
      const { tokens, user } = await apiFetch<LoginResponse>('/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      })
      // Cross-tab logout guard: while we were in-flight, another tab may
      // have called logout — persisted state then has refreshToken=null.
      // Applying our rotated tokens here would resurrect a killed session.
      // Drop silently; the storage event will have already rehydrated us.
      if (typeof window !== 'undefined') {
        const stored = readPersistedRefreshToken()
        if (stored === null) {
          return
        }
      }
      set(applyTokens(tokens, user))
      await get().fetchCurrentUser().catch(() => {})
      try {
        const permissions = await apiFetch<ResolvedPermission[]>('/rbac/me/permissions')
        set({ permissions })
      } catch {
        // Non-blocking
      }
    } catch {
      // Truly-simultaneous race: A and B both sent /auth/refresh with R0,
      // A won, B got TOKEN_REVOKED. Wait for A's storage write to land via
      // either the storage event (fast path) or a backstop poll, up to 2s.
      const rotated = await waitForRotatedRefreshToken(attemptedRefreshToken)
      if (rotated) {
        await useAuthStore.persist.rehydrate()
        return
      }
      set(CLEARED)
    }
  },

  fetchPermissions: async () => {
    try {
      const permissions = await apiFetch<ResolvedPermission[]>('/rbac/me/permissions')
      set({ permissions })
    } catch {
      // Keep existing permissions on failure
    }
  },

  fetchCurrentUser: async () => {
    const user = await apiFetch<AuthUser>('/users/me')
    set({ user })
    // Rehydrate funnel: l'attribuzione del DB è autoritativa per l'utente
    // loggato (cross-device + cambio utente sullo stesso browser senza logout).
    void import('@/lib/analytics').then((m) => m.hydrateFromUser(user.id, user.attribution)).catch(() => {})
  },

  clearAuth: () => set(CLEARED),
}), {
  name: STORAGE_KEY,
  partialize: (state) => ({
    accessToken: state.accessToken,
    refreshToken: state.refreshToken,
    user: state.user,
    permissions: state.permissions,
    isAuthenticated: state.isAuthenticated,
  }),
}))

// Cross-tab sync: when another tab rotates the refresh token (or logs out),
// localStorage changes fire a `storage` event in this tab. Re-hydrate the
// store so background queries pick up the new accessToken/refreshToken
// instead of calling /auth/refresh with the now-revoked one.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) {
      void useAuthStore.persist.rehydrate()
    }
  })
}
